/**
 * /payroll-l4/compliance — Compliance Queue (Step 5).
 *
 * Lists every open / overridden / resolved prevailing-wage violation for
 * the active entity. Admin actions:
 *
 *   • Override (with reason)         → pw_violations.status = 'overridden'
 *   • Auto-fix shortfall (cash-in-lieu)
 *                                     → writes paycheck_cash_fringe row,
 *                                       resolves the linked violation
 *   • Re-run engine on a pay run     → wipes & rebuilds checks/violations
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import GavelIcon from "@mui/icons-material/Gavel";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";
import { useToast } from "@/components/Toaster";
import type {
  PWComplianceRunResult,
  PWViolation,
  PWViolationStatus,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/payroll-l4/compliance")({
  component: ComplianceQueuePage,
});

const STATUS_COLOR: Record<PWViolationStatus, "default" | "error" | "success" | "warning"> = {
  open: "error",
  resolved: "success",
  overridden: "warning",
  auto_fixed: "success",
};

function ComplianceQueuePage() {
  const qc = useQueryClient();
  const toast = useToast();

  const [statusFilter, setStatusFilter] = useState<PWViolationStatus | "">("open");
  const [payRunId, setPayRunId] = useState("");
  const [overrideTarget, setOverrideTarget] = useState<PWViolation | null>(null);
  const [misclassResult, setMisclassResult] = useState<{
    suspects: { employee_label: string; project_label?: string; classification: string; hours?: string; suspect_type: string; confidence: number; reasoning: string }[];
    summary: string;
    ai: string;
  } | null>(null);

  const { data: violations = [], isLoading, error } = useQuery({
    queryKey: ["payroll-l4", "violations", statusFilter, payRunId],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set("status_filter", statusFilter);
      if (payRunId.trim()) qs.set("pay_run_id", payRunId.trim());
      return (await api(
        `/api/payroll-l4/violations${qs.toString() ? "?" + qs : ""}`,
      )) as PWViolation[];
    },
  });

  const runEngine = useMutation({
    mutationFn: async (prId: string) =>
      (await api(
        `/api/payroll-l4/pay-runs/${prId}/compliance/run`,
        { method: "POST", body: {} },
      )) as PWComplianceRunResult,
    onSuccess: (r) => {
      toast.success(
        `Engine ran: ${r.checks_written} check${r.checks_written === 1 ? "" : "s"}, `
        + `${r.violations_written} violation${r.violations_written === 1 ? "" : "s"} written.`,
      );
      qc.invalidateQueries({ queryKey: ["payroll-l4", "violations"] });
    },
    onError: (e: unknown) => toast.error((e as Error).message),
  });

  const autoFix = useMutation({
    mutationFn: async (checkId: string) =>
      (await api(
        `/api/payroll-l4/compliance-checks/${checkId}/add-cash-in-lieu`,
        { method: "POST", body: {} },
      )),
    onSuccess: () => {
      toast.success("Cash-in-lieu line added; re-run engine to confirm pass.");
      qc.invalidateQueries({ queryKey: ["payroll-l4", "violations"] });
    },
    onError: (e: unknown) => toast.error((e as Error).message),
  });

  const overrideMut = useMutation({
    mutationFn: async (args: { id: string; reason: string }) =>
      (await api(
        `/api/payroll-l4/violations/${args.id}/override`,
        { method: "POST", body: { reason: args.reason } },
      )),
    onSuccess: () => {
      toast.success("Violation overridden.");
      setOverrideTarget(null);
      qc.invalidateQueries({ queryKey: ["payroll-l4", "violations"] });
    },
    onError: (e: unknown) => toast.error((e as Error).message),
  });

  const misclassMut = useMutation({
    mutationFn: async (prId: string) =>
      (await api(
        `/api/payroll-l4/pay-runs/${prId}/ai/misclassification-scan`,
        { method: "POST", body: {} },
      )) as { suspects: { employee_label: string; project_label?: string; classification: string; hours?: string; suspect_type: string; confidence: number; reasoning: string }[]; summary: string; ai: string },
    onSuccess: (r) => setMisclassResult(r),
    onError: (e: unknown) => toast.error((e as Error).message),
  });

  return (
    <Box sx={{ p: 3, maxWidth: 1500, mx: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Compliance Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Workers paid below the required prevailing wage on a Davis-Bacon job.
            Fix a shortfall with <strong>Auto-fix</strong> (adds a cash-in-lieu
            payment so the worker is made whole), or <strong>Override</strong>{" "}
            with a documented reason. Clear all blocking items here before you can
            approve the pay run.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <HelpHint
              docPath="payroll-l4/step-5-compliance-engine.md"
              label="How prevailing-wage compliance works"
            />
          </Box>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            select label="Status" size="small" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as PWViolationStatus | "")}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="overridden">Overridden</MenuItem>
            <MenuItem value="auto_fixed">Auto-fixed</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
          </TextField>
          <TextField
            label="Pay-run ID" size="small" value={payRunId}
            onChange={(e) => setPayRunId(e.target.value)}
            placeholder="filter by id"
            sx={{ width: 220 }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              const id = payRunId.trim();
              if (!id) {
                toast.error("Enter a pay-run ID first.");
                return;
              }
              runEngine.mutate(id);
            }}
            disabled={runEngine.isPending}
          >
            Re-run engine
          </Button>
          <Button
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => {
              const id = payRunId.trim();
              if (!id) {
                toast.error("Enter a pay-run ID first.");
                return;
              }
              setMisclassResult(null);
              misclassMut.mutate(id);
            }}
            disabled={misclassMut.isPending}
          >
            AI Misclassification Scan
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{String((error as Error).message)}</Alert>}

      {misclassResult && (
        <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AutoAwesomeIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight={600}>AI Misclassification Scan</Typography>
            </Stack>
            <Button size="small" onClick={() => setMisclassResult(null)}>Dismiss</Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" mb={1}>{misclassResult.summary}</Typography>
          {misclassResult.suspects.length === 0 ? (
            <Alert severity="success">No suspect classifications found.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Classification</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Conf.</TableCell>
                  <TableCell>Reasoning</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {misclassResult.suspects.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ fontWeight: 600 }}>{s.employee_label}</TableCell>
                    <TableCell>{s.classification}</TableCell>
                    <TableCell>
                      <Chip label={s.suspect_type.replace(/_/g, " ")} size="small" color="warning" />
                    </TableCell>
                    <TableCell align="right">{Math.round(s.confidence * 100)}%</TableCell>
                    <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{s.reasoning}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      {isLoading ? <CircularProgress /> : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Classification</TableCell>
                <TableCell align="right">Hours</TableCell>
                <TableCell align="right">Required</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Shortfall</TableCell>
                <TableCell align="right">$ at risk</TableCell>
                <TableCell>Resolution</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {violations.map((v) => (
                <TableRow key={v.id}>
                  <TableCell>{v.violation_type.replace(/_/g, " ")}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={v.severity}
                      color={v.severity === "critical" ? "error" : v.severity === "warning" ? "warning" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={v.status}
                      color={STATUS_COLOR[v.status]}
                    />
                  </TableCell>
                  <TableCell>{v.project_name ?? (v.project_id ? v.project_id.slice(0, 8) : "—")}</TableCell>
                  <TableCell>{v.employee_name ?? (v.employee_id ? v.employee_id.slice(0, 8) : "—")}</TableCell>
                  <TableCell>{v.classification ?? "—"}</TableCell>
                  <TableCell align="right">{v.hours ?? "—"}</TableCell>
                  <TableCell align="right">${v.required_total ?? "—"}</TableCell>
                  <TableCell align="right">${v.paid_total ?? "—"}</TableCell>
                  <TableCell align="right">${v.shortfall ?? "—"}</TableCell>
                  <TableCell align="right">
                    {v.amount_at_risk ? `$${v.amount_at_risk}` : "—"}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260, verticalAlign: "top", py: 1.25 }}>
                    <Tooltip title={v.resolution_note ?? v.message} placement="top-start" arrow>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          lineHeight: 1.4,
                          color: v.resolution_note ? "text.primary" : "text.secondary",
                          cursor: "default",
                        }}
                      >
                        {v.resolution_note ?? v.message}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {v.status === "open" && (
                      <Stack direction="row" spacing={1}>
                        {v.violation_type === "underpayment" && v.compliance_check_id && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disableElevation
                            startIcon={<AutoFixHighIcon sx={{ fontSize: 15 }} />}
                            onClick={() => autoFix.mutate(v.compliance_check_id!)}
                            disabled={autoFix.isPending}
                            sx={{
                              whiteSpace: "nowrap",
                              textTransform: "none",
                              fontWeight: 600,
                              px: 1.25,
                              "&:hover": { boxShadow: "none" },
                            }}
                          >
                            Auto-fix
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          startIcon={<GavelIcon sx={{ fontSize: 15 }} />}
                          onClick={() => setOverrideTarget(v)}
                          sx={{
                            whiteSpace: "nowrap",
                            textTransform: "none",
                            fontWeight: 500,
                            px: 1.25,
                            borderColor: "divider",
                            color: "text.secondary",
                            "&:hover": { borderColor: "text.secondary", backgroundColor: "action.hover" },
                          }}
                        >
                          Override
                        </Button>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {violations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No violations match this filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <OverrideDialog
        target={overrideTarget}
        onClose={() => setOverrideTarget(null)}
        onSubmit={(reason) =>
          overrideTarget && overrideMut.mutate({ id: overrideTarget.id, reason })
        }
        pending={overrideMut.isPending}
      />
    </Box>
  );
}

function OverrideDialog({
  target,
  onClose,
  onSubmit,
  pending,
}: {
  target: PWViolation | null;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  pending: boolean;
}) {
  const [reason, setReason] = useState("");
  return (
    <Dialog
      open={!!target}
      onClose={() => {
        setReason("");
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Override violation</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Reason is captured + persisted on the violation row. Use this when
          the worker has been made whole outside this pay run, or when the
          shortfall is being investigated.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          rows={3}
          size="small"
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setReason(""); onClose(); }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!reason.trim() || pending}
          onClick={() => {
            onSubmit(reason);
            setReason("");
          }}
        >
          {pending ? <CircularProgress size={18} /> : "Override"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
